<template>
  <div>
    <!-- Payment Method Selection -->
    <div v-if="selectedTableForPayment && !selectedTableForPayment.pre_charge_payment_method"
      class="payment-method-list">
      <div class="payment-method-item" :class="{ 'disabled': loading }" @click="!loading && selectPaymentMethodType(1)">
        <i v-if="loading" class="fas fa-spinner fa-spin fa-2x"></i>
        <i v-else class="fas fa-money-bill-wave fa-2x"></i>
        <span>{{ loading ? 'Saving...' : 'Cash' }}</span>
      </div>
      <div class="payment-method-item" :class="{ 'disabled': loading }" @click="!loading && selectPaymentMethodType(3)">
        <i class="fa fa-university fa-2x"></i>
        <span>Bank Transfer</span>
      </div>
    </div>

    <!-- Bank Selection (shown when Bank Transfer is selected) -->
    <div v-if="selectedTableForPayment && selectedTableForPayment.pre_charge_payment_method == 3">
      <div class="bank-selection-section">
        <label class="selection-label">Select Bank</label>
        <div class="bank-logo-grid">
          <div v-for="(bank, index) in bankNameOptions" :key="index" class="bank-logo-item"
            :class="{ 'selected': selectedTableForPayment.pre_charge_bank_id === bank.pre_charge_bank_id }"
            @click="selectBank(bank)">
            <img class="bank-logo" :src="bank.bank_logo" @error="handleImageError" :alt="bank.label" />
            <span class="bank-name">{{ bank.label }}</span>
          </div>
        </div>
      </div>

      <div v-if="selectedTableForPayment.pre_charge_bank_id" class="account-selection-section">
        <label class="selection-label">Select Account</label>
        <div class="account-list">
          <div v-for="(account, index) in accountOptions" :key="index" class="account-item"
            :class="{ 'selected': selectedTableForPayment.pre_charge_bank_account_number === account.value }"
            @click="selectAccount(account)">
            <div class="account-info">
              <div class="account-bank">{{ selectedBankName }}</div>
              <div class="account-name">{{ account.name }}</div>
              <div class="account-number">{{ account.label }}</div>
            </div>
            <i v-if="selectedTableForPayment.pre_charge_bank_account_number === account.value"
              class="fas fa-check-circle"></i>
          </div>
        </div>

      </div>

      <div class="modal-actions">
        <button class="btn-back" @click="goBackToPaymentMethods">
          <i class="fas fa-arrow-left"></i> Back
        </button>
        <button class="btn-confirm" :disabled="!selectedTableForPayment.pre_charge_bank_account_number || loading"
          @click="confirmPaymentMethod">
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-check"></i>
          {{ loading ? 'Saving...' : 'Confirm' }}
        </button>
      </div>
      <Spin size="large" fix v-if="loadingBankProfiles"> </Spin>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'PaymentMethodSelection',
  props: {
    table: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedTableForPayment: null,
      bankProfiles: [],
      loadingBankProfiles: false
    }
  },
  computed: {
    bankNameOptions() {
      return this.bankProfiles.map(el => ({
        value: el.bank_id,
        label: el.bank_name,
        bank_logo: el.bank_logo,
        bank_name: el.bank_name,
        pre_charge_bank_id: el.bank_id
      }))
    },
    accountOptions() {
      if (!this.selectedTableForPayment || !this.selectedTableForPayment.pre_charge_bank_id) return [];

      let selectedBank = this.bankProfiles.find(
        b => b.bank_id == this.selectedTableForPayment.pre_charge_bank_id
      );

      if (selectedBank && selectedBank.accounts) {
        return selectedBank.accounts.map(el => ({
          value: el.account_number,
          label: el.account_number,
          name: el.account_name
        }))
      }
      return []
    },
    selectedBankName() {
      if (!this.selectedTableForPayment || !this.selectedTableForPayment.pre_charge_bank_id) return null;

      let selectedBank = this.bankProfiles.find(
        b => b.bank_id == this.selectedTableForPayment.pre_charge_bank_id
      );

      return selectedBank ? selectedBank.bank_name : null;
    }
  },
  watch: {
    table: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.selectedTableForPayment = { ...newVal }
        }
      }
    }
  },
  methods: {
    ...mapActions('restaurantSale/counterCheckout', ['bankProfile']),
    selectPaymentMethodType(paymentMethodId) {
      if (paymentMethodId === 1) {
        // Emit confirm without mutating local state — keep the list visible while parent loads
        this.$emit('confirm', {
          ...this.selectedTableForPayment,
          pre_charge_payment_method: 1,
          pre_charge_bank_id: null,
          pre_charge_bank_account_number: null,
        });
      } else if (paymentMethodId === 3) {
        // Bank Transfer - show bank selection
        this.selectedTableForPayment.pre_charge_payment_method = 3;
        this.selectedTableForPayment.pre_charge_bank_id = null;
        this.selectedTableForPayment.pre_charge_bank_account_number = null;
        this.fetchBankProfiles();
      }
    },
    selectBank(bank) {
      this.selectedTableForPayment.pre_charge_bank_id = bank.value;
      this.selectedTableForPayment.pre_charge_bank_account_number = null;
    },
    selectAccount(account) {
      this.selectedTableForPayment.pre_charge_bank_account_number = account.value;
    },
    confirmPaymentMethod() {
      if (this.selectedTableForPayment.pre_charge_payment_method === 3 && this.selectedTableForPayment.pre_charge_bank_account_number) {
        // All data is already set on the table object
        this.$emit('confirm', this.selectedTableForPayment);
      }
    },
    goBackToPaymentMethods() {
      // Clear table bank fields when going back
      this.selectedTableForPayment.pre_charge_payment_method = null;
      this.selectedTableForPayment.pre_charge_bank_id = null;
      this.selectedTableForPayment.pre_charge_bank_account_number = null;
    },
    handleImageError(event) {
      event.target.src = require('@/assets/logo.png');
    },
    fetchBankProfiles() {
      this.loadingBankProfiles = true
      this.bankProfile()
        .then(response => {
          this.bankProfiles = response.data
        })
        .catch(error => {
          console.error('Error fetching bank profiles:', error)
        })
        .finally(() => {
          this.loadingBankProfiles = false
        })
    }
  }
}
</script>

<style scoped>
/* Payment Method Modal Styles */
.payment-method-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  padding: 10px;
}

.payment-method-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px 15px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.payment-method-item:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.payment-method-item i {
  color: #666;
  margin-bottom: 10px;
  transition: color 0.3s ease;
}

.payment-method-item:hover i {
  color: #1890ff;
}

.payment-method-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.payment-method-item span {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  text-align: center;
}

/* Bank Selection Styles */
.bank-selection-section,
.account-selection-section {
  margin-bottom: 20px;
}

.selection-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
}

.bank-logo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.bank-logo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.bank-logo-item:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.bank-logo-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.bank-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 4px;
  margin-bottom: 8px;
}

.bank-name {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  text-align: center;
  word-break: break-word;
}

.account-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.account-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.account-item:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
}

.account-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.account-info {
  flex: 1;
}

.account-bank {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.account-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.account-number {
  font-size: 13px;
  color: #666;
}

.account-item i {
  color: #1890ff;
  font-size: 20px;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e8e8e8;
}

.btn-back,
.btn-confirm {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-back {
  background-color: #f5f5f5;
  color: #666;
}

.btn-back:hover {
  background-color: #e8e8e8;
  color: #333;
}

.btn-confirm {
  background-color: #1890ff;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background-color: #40a9ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
}

.btn-confirm:disabled {
  background-color: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}
</style>
